import {
  HydratedStoryPoint,
  isAnswerGatePage,
  messageDeliveryFor,
  type StorySettings,
} from 'magical-trails';

type StatusPass = {
  updatedStoryPoints: HydratedStoryPoint[];
  unlockedPointsQueue: string[];
};

/**
 * One top-to-bottom pass. `completedCodes` is what counts as completed for
 * `completedPoints` rules: those rules can name a point LATER in the list,
 * which a single pass hasn't reached yet, so the caller feeds each pass the
 * completions the previous one found (see setStoryPointsStatus).
 */
function statusPass(
  storyPoints: HydratedStoryPoint[],
  completedCodes: ReadonlySet<string>,
  settings: StorySettings | null | undefined
): StatusPass {
  let allPrecedingPointsCompleted: boolean = true;
  let unlockedPointsQueue: string[] = [];
  const knownCodes = new Set(storyPoints.map((storyPoint) => storyPoint.code));

  function getStatus(storyPoint: HydratedStoryPoint):
    | undefined
    | 'untouched'
    | 'awaitingQuestionAnswer' // We'll call this `inProgress` at some point...
    | 'locked'
    | 'completed'
    | 'hidden'
    | 'geofenced' {
    // Rules
    if (storyPoint.config?.unlockRules) {
      if (
        storyPoint.config?.unlockRules.type === 'completedAllPrecedingPoints'
      ) {
        if (allPrecedingPointsCompleted === false) {
          if (storyPoint.config?.hiddenIfRulesUnmet === true) {
            return 'hidden';
          }
          return 'locked';
        }
      }
      if (storyPoint.config?.unlockRules.type === 'completedPoints') {
        // A required point that no longer exists (deleted by a live edit)
        // is ignored, not waited on forever.
        const unmet = storyPoint.config.unlockRules.requiredPoints.some(
          (required) =>
            knownCodes.has(required) && !completedCodes.has(required)
        );
        if (unmet && storyPoint.status !== 'completed') {
          allPrecedingPointsCompleted = false;
          return storyPoint.config?.hiddenIfRulesUnmet === true
            ? 'hidden'
            : 'locked';
        }
      }
    }
    // A question page the player hasn't answered yet must never be reported
    // as "completed" just because it's the point's last (or only) page and
    // the point has been opened. Pages.tsx marks `viewed` true as soon as a
    // point is opened, so without this guard a single/last-page question
    // point flipped to 'completed' the instant it was opened — before the
    // question was ever answered — letting
    // `unlockRules: 'completedAllPrecedingPoints'` points unlock (and the
    // question be skipped) with no answer given.
    const currentPageData =
      typeof storyPoint.currentPage === 'number'
        ? storyPoint.pages?.[storyPoint.currentPage]
        : undefined;
    // A Number Lock is the same kind of gate: `userAnswer` is only set once
    // the correct code has been entered.
    const isUnansweredQuestionPage =
      isAnswerGatePage(currentPageData) &&
      currentPageData.userAnswer === undefined;

    if (
      // Completed story points stay completed
      storyPoint.status === 'completed' ||
      // We don't care if the intro was completed or not — unless it arrives
      // as a message (phone shell), where it is a conversation like any
      // other and may be waiting on the player's reply.
      (storyPoint.type === 'introduction' &&
        !messageDeliveryFor(storyPoint, settings)) ||
      (storyPoint.viewed === true &&
        storyPoint?.currentPage === (storyPoint.pages?.length || 0) - 1 &&
        !isUnansweredQuestionPage)
    ) {
      return 'completed';
    }

    if (
      typeof storyPoint.currentPage === 'number' &&
      storyPoint?.pages?.[storyPoint.currentPage]?.type === 'locationCheck'
    ) {
      allPrecedingPointsCompleted = false;
      return 'geofenced';
    }

    if (!storyPoint.viewed === true) {
      if (
        // A `completedPoints` point unlocks on its own list, wherever it
        // sits in the order.
        (allPrecedingPointsCompleted === true ||
          storyPoint.config?.unlockRules?.type === 'completedPoints') &&
        storyPoint.code &&
        (storyPoint.status === 'locked' || storyPoint.status === 'hidden') &&
        (storyPoint.type === 'narrative' || storyPoint.type === 'conclusion')
      ) {
        unlockedPointsQueue.push(storyPoint.code);
      }
      allPrecedingPointsCompleted = false;
      return 'untouched';
    }

    // `.some(...)` (a boolean), not `.filter(...)` (an array — which is
    // truthy even when EMPTY). The previous `.filter` meant any viewed point
    // with a defined `pages` array — including plain narratives with no
    // question page at all — was reported `awaitingQuestionAnswer`.
    const hasQuestion = storyPoint.pages?.some(
      (page) =>
        page?.type?.toLowerCase().includes('question') || isAnswerGatePage(page)
    );

    if (storyPoint.viewed === true && hasQuestion) {
      allPrecedingPointsCompleted = false;
      return 'awaitingQuestionAnswer';
    }

    return undefined;
  }

  return {
    updatedStoryPoints: storyPoints.map((storyPoint) => {
      return {
        ...storyPoint,
        status: getStatus(storyPoint),
      };
    }),
    unlockedPointsQueue,
  };
}

function completedCodesOf(storyPoints: HydratedStoryPoint[]): Set<string> {
  return new Set(
    storyPoints
      .filter((storyPoint) => storyPoint.status === 'completed')
      .map((storyPoint) => storyPoint.code)
  );
}

function setStoryPointsStatus(
  storyPoints: HydratedStoryPoint[],
  /** The story's settings; only the phone shell reads them (see above). */
  settings?: StorySettings | null
): StatusPass {
  // Every pass reads the ORIGINAL points (their prior status decides what
  // counts as newly unlocked); only the completed set moves between passes.
  // Completions only ever grow, so this settles — the cap is a backstop for
  // a rule cycle (A needs B, B needs A), which simply stays locked.
  let completedCodes = completedCodesOf(storyPoints);
  let pass = statusPass(storyPoints, completedCodes, settings);
  for (let i = 0; i < storyPoints.length; i += 1) {
    const next = completedCodesOf(pass.updatedStoryPoints);
    if (next.size === completedCodes.size) break;
    completedCodes = next;
    pass = statusPass(storyPoints, completedCodes, settings);
  }
  return pass;
}

export default setStoryPointsStatus;
